import { Form as FormikForm, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { Col, Form, Nav, Row, Stack } from 'react-bootstrap';
import * as Yup from 'yup';
import { DateField } from '../../components/tables/filterForms/DateField';
import { useGetAllOrdersQuery } from '../../services/endpoints/orders/orders';
import { GetQueryParams } from '../../types/api.type';
import { Order } from '../../types/orders.type';
import SessionCard from './SessionCard';
import { getCurrentUser } from '../../services/helper';

const calculateDate = (offset: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + offset);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};

const todayISO = () => calculateDate(0);

const createFilter = (field: string, value: string, operator: string) => ({
  operator,
  field,
  value
});

type OrdersTab = 'upcoming' | 'past';

// Default ranges per tab
const getDefaultRange = (tab: OrdersTab): { start: string; end: string } => {
  if (tab === 'upcoming') {
    return { start: todayISO(), end: calculateDate(365) }; // today through next year
  }
  return { start: calculateDate(-365), end: todayISO() }; // last year through today
};

const Orders: React.FC = () => {
  const currentUser = getCurrentUser();
  const [orders, setOrders] = useState<Order[]>([]);
  const [pageSize, setPageSize] = useState(5);
  const [activeTab, setActiveTab] = useState<OrdersTab>('upcoming');

  const initialRange = getDefaultRange('upcoming');
  const [dateRange, setDateRange] = useState(initialRange);

  const buildFilters = (
    range: { start: string; end: string },
    tab: OrdersTab
  ): GetQueryParams => ({
    // Parent-facing orders list must show paid enrollments only. getAllOrders
    // does NOT default to completed when no scope is sent, it returns all
    // orders, so incomplete (in-cart, unpaid) rows would render here as if the
    // child were enrolled. Same reason CourseRegister.tsx sets this explicitly.
    scope: 'completed',
    data: {
      entries: pageSize,
      filterBy: [
        ...(currentUser?.id
            ? [createFilter('parent_id', String(currentUser.id), 'equals')]
            : []),
        // Upcoming: include in-progress sessions (started before today, still ending
        // on/after range.start). Past: keep start/end bounds on starts_on/ends_on.
        ...(tab === 'upcoming'
            ? [
                createFilter('session_ends_on', range.start, 'greater_equal_than'),
                createFilter('session_starts_on', range.end, 'lesser_equal_than')
              ]
            : [
                createFilter('session_starts_on', range.start, 'greater_equal_than'),
                createFilter('session_ends_on', range.end, 'lesser_equal_than')
              ])
      ]
    }
  });

  const [filters, setFilters] = useState<GetQueryParams>(
    buildFilters(initialRange, 'upcoming')
  );
  const { data, isLoading } = useGetAllOrdersQuery(filters);

  useEffect(() => {
    if (data?.results) {
      setOrders(data.results);
    }
  }, [data]);

  useEffect(() => {
    setFilters(buildFilters(dateRange, activeTab));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, pageSize, activeTab]);

  // When tab changes, reset to that tab's default range
  const handleTabChange = (tab: OrdersTab) => {
    setActiveTab(tab);
    setDateRange(getDefaultRange(tab));
  };

  const validationSchema = Yup.object({
    session_starts_on: Yup.date()
        .required('Start date is required')
        .typeError('Must be a valid date'),
    session_ends_on: Yup.date()
        .required('End date is required')
        .typeError('Must be a valid date')
        .min(Yup.ref('session_starts_on'), 'End date must be later than start date')
  });

  const handleSubmit = (values: any) => {
    setDateRange({ start: values.session_starts_on, end: values.session_ends_on });
  };

  let numPages = data?.count !== 0 ? Math.ceil(data?.count! / data?.entries!) : 1;
  if (isNaN(numPages)) numPages = 1;

  return (
      <div className="container-fluid pt-3 pt-md-0">
        <Nav
            variant="underline"
            activeKey={activeTab}
            onSelect={(k) => handleTabChange((k as OrdersTab) ?? 'upcoming')}
            className="mb-3 justify-content-center"
        >
          <Nav.Item>
            <Nav.Link
                eventKey="upcoming"
                className="text-uppercase fw-bold me-3"
            >
              Upcoming
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
                eventKey="past"
                className="text-uppercase fw-bold"
            >
              Past
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Stack gap={2} direction={'horizontal'}>
          <Formik
              key={activeTab} // re-mount on tab change so initial values reset
              initialValues={{
                session_starts_on: dateRange.start,
                session_ends_on: dateRange.end
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
          >
            {({ handleSubmit, setFieldValue }) => (
                <FormikForm onSubmit={handleSubmit}>
                  <Stack direction={'horizontal'} gap={3}>
                    <DateField
                        name="session_starts_on"
                        label="Start Date"
                        handleSubmit={handleSubmit}
                        setFieldValue={setFieldValue}
                        default_Value={dateRange.start}
                    />
                    <DateField
                        name="session_ends_on"
                        label="End Date"
                        handleSubmit={handleSubmit}
                        setFieldValue={setFieldValue}
                        default_Value={dateRange.end}
                    />
                  </Stack>
                </FormikForm>
            )}
          </Formik>
        </Stack>

        {!isLoading && orders.length === 0 && (
            <p className="text-center text-muted my-5">
              No {activeTab} orders to show.
            </p>
        )}

        {orders.map((order, index) => (
            <SessionCard
                key={index}
                order={order}
                course={order.course}
                edit={() => {}}
            />
        ))}

        {orders.length > 0 && (
            <>
              <hr />
              <Row className="align-items-center p-lg-4 my-lg-0 my-3">
                <Col xs={12}>
                  <Stack
                    direction="horizontal"
                    gap={2}
                    className="align-items-center sfc-table-page-size"
                  >
                <span
                    className="fw-normal ms-lg-0 ms-2 flex-shrink-0"
                    style={{ fontSize: '.875rem', color: '#344054' }}
                >
                  Viewing:
                </span>
                    <Form.Select
                        id="count"
                        value={pageSize}
                        onChange={(e) => setPageSize(+e.target.value)}
                        className="pagination py-0"
                    >
                      {[5, 10, 20, 50].map((item) => (
                          <option value={item} key={item}>
                            {item}
                          </option>
                      ))}
                    </Form.Select>
                    <span
                        className="d-none d-lg-block"
                        style={{ fontSize: '.875rem', color: '#344054' }}
                    >
                  {`Page ${data?.page ? data.page : 0} of ${numPages}`}
                </span>
                  </Stack>
                </Col>
              </Row>
            </>
        )}
      </div>
  );
};

export default Orders;